import { type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  chipKeyLabel,
  readFilters,
  type EventFilters,
} from "./vocab.ts";

/**
 * The standing-filter bar (task 11.12; design 12.2 "standing filters", 12.3 honest affordances). It
 * presents every ACTIVE filter — the ten named standing filters AND the drill chips another screen
 * arrived with — as removable chips, so a drill from any screen lands with its intent VISIBLE and
 * the operator can read, adjust or clear it. The URL is the single source of truth (so a drill is a
 * link and the browser back button works): removing a chip rewrites `useSearchParams`, which the
 * screen re-reads into {@link EventFilters}.
 *
 * The named standing filters that ARE contract query params are shown with a "sent to the server"
 * affordance; the drill chips the `/events` contract does NOT accept are shown as "applied here"
 * (client-side) so the operator understands why those narrow the visible events without a refetch
 * (and so an honest reader never pretends an unsupported param reached the server). Both kinds are
 * removable the same way.
 *
 * This bar renders only the ACTIVE filters as chips plus a "clear all" action; it is intentionally
 * not a full query builder (adding brand-new filters from scratch is a later affordance) — its job
 * is to make every arriving filter legible and clearable, which is what the drill-ins need.
 */
export function FilterBar(props: { filters: EventFilters }): ReactNode {
  const [params, setParams] = useSearchParams();
  const { standing, chips } = props.filters;

  // Build the flat list of visible chips: the named standing filters (list filters expand to one
  // chip per value so each is independently removable), then the client-side drill chips.
  const standingChips: StandingChip[] = [];
  for (const value of standing.component) standingChips.push(listChip("component", value));
  for (const value of standing.event) standingChips.push(listChip("event", value));
  for (const value of standing.severity) standingChips.push(listChip("severity", value));
  if (standing.agent !== null) standingChips.push(scalarChip("agent", standing.agent));
  if (standing.decision !== null) standingChips.push(scalarChip("decision", standing.decision));
  if (standing.reason !== null) standingChips.push(scalarChip("reason", standing.reason));
  if (standing.operation !== null) standingChips.push(scalarChip("operation", standing.operation));
  if (standing.gateway !== null) standingChips.push(scalarChip("gateway", standing.gateway));
  if (standing.trace !== null) standingChips.push(scalarChip("trace", standing.trace));

  const hasAny = standingChips.length > 0 || chips.length > 0;

  if (!hasAny) {
    return (
      <section className="evt-filters" aria-label="Standing filters">
        <p className="evt-filters__empty">
          No filters applied. Showing all redacted events in the window. Drill in from another
          screen, or open an event for its detail.
        </p>
      </section>
    );
  }

  const removeStanding = (chip: StandingChip): void => {
    const next = new URLSearchParams(params);
    if (chip.kind === "list") {
      // A list filter is comma-joined under one key; drop just this value, keep the rest.
      const remaining = splitList(next.get(chip.key)).filter((v) => v !== chip.value);
      if (remaining.length === 0) next.delete(chip.key);
      else next.set(chip.key, remaining.join(","));
    } else {
      next.delete(chip.key);
    }
    setParams(next, { replace: true });
  };

  const removeChip = (key: string): void => {
    const next = new URLSearchParams(params);
    next.delete(key);
    setParams(next, { replace: true });
  };

  const clearAll = (): void => {
    // Keep only the reserved paging/window/order params; drop every filter and drill chip.
    const next = new URLSearchParams();
    for (const key of ["from", "to", "cursor", "order", "limit"]) {
      const value = params.get(key);
      if (value !== null) next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  return (
    <section className="evt-filters" aria-label="Standing filters">
      <ul className="evt-filters__chips">
        {standingChips.map((chip) => (
          <li key={`${chip.key}:${chip.value}`}>
            <span className="evt-chip" data-scope="server">
              <span className="evt-chip__key">{standingLabel(chip.key)}</span>
              <span className="evt-chip__value">{chip.value}</span>
              <button
                type="button"
                className="evt-chip__remove"
                onClick={() => removeStanding(chip)}
                aria-label={`Remove the ${standingLabel(chip.key)} filter ${chip.value}`}
                title="Remove this filter"
              >
                ✕
              </button>
            </span>
          </li>
        ))}
        {chips.map((chip) => (
          <li key={`chip:${chip.key}:${chip.value}`}>
            <span
              className="evt-chip"
              data-scope="client"
              title="Applied here over the events. This filter is not a server-side events parameter"
            >
              <span className="evt-chip__key">{chipKeyLabel(chip.key)}</span>
              <span className="evt-chip__value">{chip.value}</span>
              <span className="evt-chip__note" aria-hidden="true">
                applied here
              </span>
              <button
                type="button"
                className="evt-chip__remove"
                onClick={() => removeChip(chip.key)}
                aria-label={`Remove the ${chipKeyLabel(chip.key)} filter ${chip.value}`}
                title="Remove this filter"
              >
                ✕
              </button>
            </span>
          </li>
        ))}
      </ul>
      <button type="button" className="evt-filters__clear" onClick={clearAll}>
        Clear all filters
      </button>
      {chips.length > 0 && (
        <p className="evt-filters__hint" role="note">
          Filters marked “applied here” narrow the events in your browser; they are not parameters
          the events API accepts, so they do not change what the server returned.
        </p>
      )}
    </section>
  );
}

/** A visible chip for one named standing filter value. */
interface StandingChip {
  readonly kind: "list" | "scalar";
  readonly key: string;
  readonly value: string;
}

function listChip(key: string, value: string): StandingChip {
  return { kind: "list", key, value };
}
function scalarChip(key: string, value: string): StandingChip {
  return { kind: "scalar", key, value };
}

/** The human label for a named standing-filter key. */
function standingLabel(key: string): string {
  switch (key) {
    case "component":
      return "Component";
    case "event":
      return "Event";
    case "severity":
      return "Severity";
    case "agent":
      return "Agent";
    case "decision":
      return "Decision";
    case "reason":
      return "Reason";
    case "operation":
      return "Operation";
    case "gateway":
      return "Gateway";
    case "trace":
      return "Trace";
    default:
      return key;
  }
}

function splitList(value: string | null): readonly string[] {
  if (value === null) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** Re-export the reader so the screen imports both from one module if it prefers. */
export { readFilters };
